import { useEffect, useLayoutEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";

import BackgroundDecor from "./components/BackgroundDecor";
import ScrollIndicator from "./components/ScrollIndicator";
import Navigation from "./components/Navigation";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import Services from "./components/Services";
import Method from "./components/Method";
import Projects from "./components/Projects";
import WhyUs from "./components/WhyUs";
import Stack from "./components/Stack";
import FAQ from "./components/FAQ";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import Seo from "./components/Seo";
import CookieConsent from "./components/CookieConsent";
import { Analytics } from "@vercel/analytics/react";

import Pizzeria from "./pages/projects/Pizzeria";
import SalonCote from "./pages/projects/SalonCote";
import PlombierPro from "./pages/projects/PlombierPro";
import Barbier from "./pages/projects/Barbier";
import MockupFrame from "./components/MockupFrame";

import Portfolio from "./pages/Portfolio";
import MockupGratuit from "./pages/MockupGratuit";
import Audit from "./pages/Audit";
import MentionsLegales from "./pages/MentionsLegales";
import Confidentialite from "./pages/Confidentialite";
import Cookies from "./pages/Cookies";
import EspaceClient from "./pages/EspaceClient";

// ─────────────────────────────────────────────────────────────
// Scroll top on route change (except when a hash is present)
function RouteScroller() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    const lenis = window.__lenis;

    if (hash) {
      const id = hash.replace(/^#/, "");
      const el = document.getElementById(id);
      if (el) {
        requestAnimationFrame(() => {
          if (lenis) lenis.scrollTo(el, { immediate: false, duration: 1.1 });
          else el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return;
      }
    }

    // Aggressive scroll-to-top: stop any Lenis momentum, jump to 0,
    // and re-assert the position over the next two frames in case
    // Lenis or layout shifts move it back.
    const jump = () => {
      if (lenis) {
        lenis.stop();
        lenis.scrollTo(0, { immediate: true, force: true, lock: true });
      }
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    jump();
    requestAnimationFrame(() => {
      jump();
      requestAnimationFrame(() => {
        jump();
        if (lenis) lenis.start();
      });
    });
  }, [pathname, hash]);

  return null;
}

// ─────────────────────────────────────────────────────────────
const Home = () => (
  <>
    <Seo
      title="Agence web · Création de site internet en France & francophonie"
      description="Agence de création de sites web basée à Paris, au service de toute la France et de la francophonie (Belgique, Suisse, Canada) : sites vitrines immersifs, rapides et optimisés SEO pour commerces, artisans et indépendants. Aperçu gratuit sans engagement."
    />
    <ScrollIndicator />
    <Navigation />
    <main data-testid="home-main" className="relative z-10">
      <Hero />
      <Marquee />
      <Services />
      <Projects />
      <WhyUs />
      <Method />
      <FAQ />
      <Stack />
      <Contact />
      <Footer />
    </main>
  </>
);

// ─────────────────────────────────────────────────────────────
function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Smoothly scroll to in-page anchors via Lenis
    const onAnchorClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href*="#"]');
      if (!a) return;
      const url = new URL(a.href, window.location.href);
      const samePath =
        url.pathname === window.location.pathname &&
        url.origin === window.location.origin;
      if (!samePath || !url.hash) return;
      const el = document.getElementById(url.hash.slice(1));
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { duration: 1.2, offset: -60 });
      // Keep the hash in the URL (without re-firing scroll)
      if (url.hash !== window.location.hash) {
        window.history.replaceState(null, "", url.hash);
      }
    };
    document.addEventListener("click", onAnchorClick);

    return () => {
      document.removeEventListener("click", onAnchorClick);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);

  return (
    <div className="App">
      <BackgroundDecor />
      <div className="noise-overlay" aria-hidden="true" />
      <Analytics />
      <CookieConsent />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "#f4f4f5",
            fontFamily: "Satoshi, sans-serif",
          },
        }}
      />
      <BrowserRouter>
        <RouteScroller />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/mockup-gratuit" element={<MockupGratuit />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/espace-client" element={<EspaceClient />} />
          <Route path="/projets/bella-napoli" element={<Pizzeria />} />
          <Route path="/projets/salon-cote" element={<SalonCote />} />
          <Route path="/projets/depann-eau-pro" element={<PlombierPro />} />
          <Route path="/projets/barbier" element={<Barbier />} />
          <Route
            path="/projets/car-wash"
            element={<MockupFrame src="/mockups/carwash/index.html" title="Car Wash — Concept ML Agence" />}
          />
          <Route
            path="/projets/cake"
            element={<MockupFrame src="/mockups/cake/index.html" title="Cake — Concept ML Agence" />}
          />
          <Route
            path="/projets/nail-studio"
            element={<MockupFrame src="/mockups/nail-studio.html" title="Nail Studio — Concept ML Agence" />}
          />
          <Route
            path="/projets/bien-etre"
            element={<MockupFrame src="/mockups/bien-etre.html" title="Studio Bien-Être — Concept ML Agence" />}
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
